import React, { useState, useEffect } from 'react';
import { X, TriangleAlert as AlertTriangle, Target, CirclePlay as PlayCircle, Sparkles } from 'lucide-react';
import pako from 'pako';
import { ReportPracticeGuide } from './ReportPracticeGuide';
import { VisualGuide } from './VisualGuide';

interface VocalBlueprintModalProps {
  isOpen: boolean;
  onClose: () => void;
  compressedData: string | null;
  title?: string;
  lyricsUrl?: string;
  vocalRecordingUrl?: string;
  leadVocalUrl?: string;
  finalRecordingUrl?: string;
  coachingResponse?: string | null;
  overallScore?: number | null;
  pitchAccuracy?: number | null;
  timingAccuracy?: number | null;
}

type TabType = 'analysis' | 'visualGuide' | 'practiceGuide';

const renderCoachingMarkdown = (text: string): JSX.Element => {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];

  const formatInline = (line: string): string =>
    line
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-white/10 px-1.5 py-0.5 rounded text-sm">$1</code>');

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-outside pl-6 space-y-2 my-4 text-white/85">
          {listItems.map((item, i) => (
            <li key={i} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  lines.forEach((line, index) => {
    if (line.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={index} className="text-lg font-bold text-kod-accent mt-6 mb-3">
          {line.substring(4)}
        </h3>
      );
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={index} className="text-xl font-bold text-kod-accent mt-8 mb-4">
          {line.substring(3)}
        </h2>
      );
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(
        <h1 key={index} className="text-2xl font-black text-white mt-8 mb-4">
          {line.substring(2)}
        </h1>
      );
    } else if (line.match(/^[-*]\s+/)) {
      listItems.push(line.replace(/^[-*]\s+/, ''));
    } else if (line.trim() === '') {
      flushList();
      if (elements.length > 0) {
        elements.push(<div key={`space-${index}`} className="h-2" />);
      }
    } else {
      flushList();
      elements.push(
        <p
          key={index}
          className="my-3 text-white/85 leading-[1.7] text-base"
          dangerouslySetInnerHTML={{ __html: formatInline(line) }}
        />
      );
    }
  });

  flushList();

  return <>{elements}</>;
};

export const VocalBlueprintModal: React.FC<VocalBlueprintModalProps> = ({
  isOpen,
  onClose,
  compressedData,
  title = 'Vocal Blueprint',
  lyricsUrl,
  vocalRecordingUrl,
  leadVocalUrl,
  finalRecordingUrl,
  coachingResponse,
  overallScore,
  pitchAccuracy,
  timingAccuracy,
}) => {
  const [jsonData, setJsonData] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const hasCoaching = !!(coachingResponse && coachingResponse.trim().length > 0);
  const [activeTab, setActiveTab] = useState<TabType>(hasCoaching ? 'analysis' : 'practiceGuide');

  useEffect(() => {
    if (!isOpen || !compressedData) {
      setJsonData(null);
      setError('');
      setActiveTab(hasCoaching ? 'analysis' : 'practiceGuide');
      return;
    }

    const decompress = async () => {
      setLoading(true);
      setError('');
      setJsonData(null);

      try {
        const binaryString = atob(compressedData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const decompressed = pako.inflate(bytes);
        const text = new TextDecoder().decode(decompressed);
        const json = JSON.parse(text);

        setJsonData(json);
      } catch (err: any) {
        console.error('Decompression error:', err);
        setError(`Failed to load vocal analysis data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    decompress();
  }, [isOpen, compressedData]);

  if (!isOpen) return null;

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    ...(hasCoaching ? [{ id: 'analysis' as TabType, label: 'Analysis', icon: <Sparkles size={16} /> }] : []),
    { id: 'practiceGuide', label: 'Practice Guide', icon: <Target size={16} /> },
    { id: 'visualGuide', label: 'Visual Guide', icon: <PlayCircle size={16} /> },
  ];

  const showTabs = jsonData || hasCoaching;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-kod-purple-800 shadow-2xl w-full h-full flex flex-col min-h-0 border-x border-kod-purple-600">
        <div className="flex items-center justify-between px-6 py-4 border-b border-kod-purple-600 bg-kod-purple-900/50">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-kod-purple-700 transition-colors text-white/70 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {showTabs && (
          <div className="flex border-b border-kod-purple-600 bg-kod-purple-900/30">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-kod-accent border-b-2 border-kod-accent bg-kod-purple-700/30'
                    : 'text-white/60 hover:text-white hover:bg-kod-purple-700/20'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className={`flex-1 min-h-0 ${activeTab === 'practiceGuide' ? 'overflow-hidden' : 'overflow-auto'}`}>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent"></div>
              <span className="ml-3 text-white/70">Loading your vocal analysis...</span>
            </div>
          )}

          {error && (
            <div className="m-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-red-400 font-medium">Error</p>
                <p className="text-red-300/80 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {!compressedData && !hasCoaching && !loading && (
            <div className="text-center py-12 text-white/50">
              No vocal analysis data available. Please score your performance first.
            </div>
          )}

          {hasCoaching && activeTab === 'analysis' && (
            <div className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
              <div className="flex items-start gap-5 mb-8">
                <img
                  src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80"
                  alt="Jocelyn"
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-kod-accent flex-shrink-0 shadow-lg"
                />
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-kod-accent font-semibold mb-1">
                    Your Coach
                  </div>
                  <div className="text-2xl font-black text-white leading-tight">Jocelyn's Analysis</div>
                  <div className="text-sm text-white/60 mt-1">A personal breakdown of your take</div>
                </div>
              </div>

              {(overallScore != null || pitchAccuracy != null || timingAccuracy != null) && (
                <div className="grid grid-cols-3 gap-3 mb-8">
                  <div className="bg-kod-purple-900/50 border border-kod-purple-600/40 rounded-xl p-4 text-center">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Overall</div>
                    <div className="text-3xl font-black text-kod-accent">
                      {overallScore != null ? Number(overallScore).toFixed(1) : '-'}
                    </div>
                  </div>
                  <div className="bg-kod-purple-900/50 border border-kod-purple-600/40 rounded-xl p-4 text-center">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Pitch</div>
                    <div className="text-3xl font-black text-white">
                      {pitchAccuracy != null ? Number(pitchAccuracy).toFixed(1) : '-'}
                    </div>
                  </div>
                  <div className="bg-kod-purple-900/50 border border-kod-purple-600/40 rounded-xl p-4 text-center">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Timing</div>
                    <div className="text-3xl font-black text-white">
                      {timingAccuracy != null ? Number(timingAccuracy).toFixed(1) : '-'}
                    </div>
                  </div>
                </div>
              )}

              <article className="prose prose-invert max-w-none">
                {renderCoachingMarkdown(coachingResponse!)}
              </article>
            </div>
          )}

          {jsonData && activeTab === 'visualGuide' && (
            <div className="h-full">
              <VisualGuide
                report={jsonData}
                lyricsUrl={lyricsUrl}
                vocalRecordingUrl={vocalRecordingUrl}
                leadVocalUrl={leadVocalUrl}
                finalRecordingUrl={finalRecordingUrl}
              />
            </div>
          )}

          {jsonData && activeTab === 'practiceGuide' && (
            <ReportPracticeGuide
              report={jsonData}
              lyricsUrl={lyricsUrl}
              vocalRecordingUrl={vocalRecordingUrl}
              leadVocalUrl={leadVocalUrl}
            />
          )}
        </div>
      </div>
    </div>
  );
};
